import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Menu,
  TextField,
  ListSubheader,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  CircularProgress,
  Popover,
} from '@mui/material';
import {
  Visibility,
  Devices,
  LocationOn,
  Share,
  Schedule,
  QrCode,
  TrendingUp,
  Assessment,
  Monitor,
  PhoneAndroid,
  Tablet,
  Computer,
  Lock,
  CheckCircleOutline,
  OpenInNew,
  FileDownload,
  StickyNote2,
  ChevronLeft,
  ChevronRight,
  ViewList,
  CalendarMonth,
  Refresh,
  ArrowBack,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { parseDate, fmtDate, fmtDateShort, fmtDateTime, fmtDateFile, tzParam, toYmdLocal, addDaysYmd } from '../utils/dateFormat';
import { toDisplayCard } from '../utils/cardDataStructure';
import { downloadMarketingAnalyticsPdf, downloadQrAnalyticsPdf } from '../utils/analyticsPdf';

const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.08)';
const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C';
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : n);

const cardBase = {
  boxShadow: CARD_SHADOW,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const sectionHeaderSx = {
  px: 3,
  py: 2.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const tableHeadCellSx = {
  fontWeight: 600,
  py: 1.5,
  fontSize: '11px',
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// World Map Component using D3 (theme-aware)
const WorldMap = ({ countryData, width, height }) => {
  const svgRef = React.useRef();
  const theme = useTheme();
  const palette = theme.palette;
  const mapBg = palette.background?.default || (theme.palette.mode === 'dark' ? '#151C28' : '#F8FAFC');
  const noDataFill = palette.grey?.[200] || palette.divider || '#e2e8f0';
  const strokeColor = palette.grey?.[400] || '#cbd5e1';
  const strokeHover = palette.grey?.[600] || '#64748b';
  const topPerformer = palette.primary?.main || '#00A7E3';
  const goodPerformer = palette.secondary?.main || '#00B3A1';
  const moderatePerformer = palette.warning?.main || '#F59E0B';
  const lowPerformer = palette.success?.main || '#10B981';
  const textSecondary = palette.text?.secondary || palette.grey?.[600] || '#64748b';

  React.useEffect(() => {
    if (!svgRef.current) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .style("background", mapBg);

    // Create projection
    const projection = d3.geoMercator()
      .scale(140)
      .center([0, 20])
      .translate([width / 2, height / 2]);

    // Create path generator
    const path = d3.geoPath().projection(projection);

    // Load world data
    d3.json("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json")
      .then(data => {
        const countries = topojson.feature(data, data.objects.countries);
        
        
        // Draw countries
        svg.selectAll("path")
          .data(countries.features)
          .enter()
          .append("path")
          .attr("d", path)
          .attr("fill", d => {
            const countryName = d.properties.name || d.properties.NAME;
            
            // Check if countryName exists and is valid
            if (!countryName || typeof countryName !== 'string') {
              return noDataFill;
            }
            
            // Check if this country has data
            
            const countryInfo = countryData.find(c => {
              if (!c.country || !countryName) return false;
              // Try different matching strategies
              const exactMatch = c.country === countryName;
              const caseMatch = c.country.toLowerCase() === countryName.toLowerCase();
              const includesMatch = countryName.toLowerCase().includes(c.country.toLowerCase()) ||
                                   c.country.toLowerCase().includes(countryName.toLowerCase());
              
              if (exactMatch || caseMatch || includesMatch) {
                return true;
              }
              return false;
            });
            
            if (countryInfo) {
              // Color based on performance level
              const maxViews = Math.max(...countryData.map(c => c.total_views));
              const performanceRatio = countryInfo.total_views / maxViews;
              
              if (performanceRatio >= 0.8) return topPerformer;
              if (performanceRatio >= 0.5) return goodPerformer;
              if (performanceRatio >= 0.2) return moderatePerformer;
              return lowPerformer;
            }
            
            return noDataFill;
          })
          .attr("stroke", strokeColor)
          .attr("stroke-width", 0.5)
          .style("cursor", "pointer")
          .on("mouseover", function(event, d) {
            d3.select(this)
              .attr("stroke", strokeHover)
              .attr("stroke-width", 1);
            
            // Show tooltip
            const countryName = d.properties.name || d.properties.NAME;
            
            // Check if countryName exists and is valid
            if (!countryName || typeof countryName !== 'string') {
              return; // Skip tooltip for invalid countries
            }
            
            const countryInfo = countryData.find(c => c.country === countryName);
            
            if (countryInfo) {
              const tooltip = d3.select("body")
                .append("div")
                .attr("class", "map-tooltip")
                .style("position", "absolute")
                .style("background", "rgba(0,0,0,0.8)")
                .style("color", "white")
                .style("padding", "8px 12px")
                .style("border-radius", "4px")
                .style("font-size", "12px")
                .style("pointer-events", "none")
                .style("z-index", "1000")
                .style("opacity", 0);
              
              tooltip.html(`
                <strong>${countryName}</strong><br/>
                Active Users: ${countryInfo.total_views}<br/>
                Percentage: ${Math.round((countryInfo.total_views / countryData.reduce((sum, c) => sum + c.total_views, 0)) * 100)}%
              `);
              
              tooltip.transition()
                .duration(200)
                .style("opacity", 1);
            }
          })
          .on("mousemove", function(event, d) {
            const tooltip = d3.select(".map-tooltip");
            if (!tooltip.empty()) {
              tooltip
                .style("left", (event.pageX + 10) + "px")
                .style("top", (event.pageY - 10) + "px");
            }
          })
          .on("mouseout", function(event, d) {
            d3.select(this)
              .attr("stroke", strokeColor)
              .attr("stroke-width", 0.5);
            
            // Remove tooltip
            d3.selectAll(".map-tooltip").remove();
          });

        // Add country labels for highlighted countries
        countryData.slice(0, 3).forEach((country, index) => {
          const countryFeature = countries.features.find(f => f.properties.NAME === country.country);
          if (countryFeature) {
            const centroid = path.centroid(countryFeature);
            svg.append("text")
              .attr("x", centroid[0])
              .attr("y", centroid[1])
              .attr("text-anchor", "middle")
              .attr("font-size", "12")
              .attr("font-weight", "600")
              .attr("fill", index === 0 ? topPerformer : goodPerformer)
              .text(country.country);
          }
        });
      })
      .catch(error => {
        console.error("Error loading world map data:", error);
        // Fallback to simple map
        svg.append("text")
          .attr("x", width / 2)
          .attr("y", height / 2)
          .attr("text-anchor", "middle")
          .attr("font-size", "16")
          .attr("fill", textSecondary)
          .text("World Map Loading...");
      });
  }, [countryData, width, height, mapBg, noDataFill, strokeColor, strokeHover, topPerformer, goodPerformer, moderatePerformer, lowPerformer, textSecondary]);

  return <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />;
};

const Analytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [marketingAnalytics, setMarketingAnalytics] = useState(null);
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('all');
  const [dateRange, setDateRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [nfcAnalytics, setNfcAnalytics] = useState(null);
  const [qrCodes, setQrCodes] = useState([]);
  const [qrAnalytics, setQrAnalytics] = useState(null);
  const [subData, setSubData] = useState(null);
  const [cardSelectOpen, setCardSelectOpen] = useState(false);
  const [cardSelectSearch, setCardSelectSearch] = useState('');

  const cardSelectSearchNorm = cardSelectSearch.trim().toLowerCase();
  const filteredCardsForSelect = useMemo(() => {
    if (!cardSelectSearchNorm) return cards;
    return cards.filter((c) => ((c.cardUrl ?? c.card_url) || '').toLowerCase().includes(cardSelectSearchNorm));
  }, [cards, cardSelectSearchNorm]);

  const filteredQrForSelect = useMemo(() => {
    if (!cardSelectSearchNorm) return qrCodes;
    return qrCodes.filter((qr) => {
      const label = (qr.label || '').toLowerCase();
      const uid = (qr.uid || '').toLowerCase();
      return label.includes(cardSelectSearchNorm) || uid.includes(cardSelectSearchNorm) || `qr:${uid}`.includes(cardSelectSearchNorm);
    });
  }, [qrCodes, cardSelectSearchNorm]);

  useEffect(() => {
    fetchAnalytics();
    fetchCards();
    fetchNfcAnalytics();
    fetchQrCodes();
    fetchSubData();
  }, [dateRange]);

  useEffect(() => {
    if (selectedCard !== 'all' && !selectedCard.startsWith('qr:')) {
      fetchMarketingAnalytics(selectedCard);
    }
    if (selectedCard.startsWith('qr:')) {
      fetchQrAnalytics(selectedCard.replace('qr:', ''));
    }
  }, [selectedCard, dateRange]);

  const cardDisplayNameForSelected = useMemo(() => {
    if (!selectedCard || selectedCard === 'all' || selectedCard.startsWith('qr:')) return '';
    const card = cards.find((c) => (c.cardUrl ?? c.card_url) === selectedCard);
    if (!card) return '';
    try {
      const raw = card.cardData ?? card.card_data;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const display = toDisplayCard(parsed || {});
      const n = (display?.name || '').trim();
      return n || '';
    } catch {
      return '';
    }
  }, [cards, selectedCard]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      let response = await fetch(`https://api.webcard.digital/api/analytics/all-cards-analytics?days=${dateRange}&${tzParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        response = await fetch(`https://api.webcard.digital/api/analytics/my-cards-analytics?days=${dateRange}&${tzParam}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('https://api.webcard.digital/api/cards/my-cards', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCards(data.cards || []);
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
    }
  };

  const fetchMarketingAnalytics = async (cardUrl) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`https://api.webcard.digital/api/analytics/marketing-analytics/${cardUrl}?days=${dateRange}&${tzParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMarketingAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching marketing analytics:', error);
    }
  };

  const fetchNfcAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const resp = await fetch(`https://api.webcard.digital/api/nfc/my-analytics?${tzParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) setNfcAnalytics(await resp.json());
    } catch (e) {
      console.error('Error fetching NFC analytics', e);
    }
  };

  const fetchQrCodes = async () => {
    try {
      const { data } = await api.get('/qr/my-codes');
      setQrCodes(data.codes || []);
    } catch (e) {
      console.error('Error fetching QR codes', e);
    }
  };

  const fetchQrAnalytics = async (uid) => {
    try {
      const { data } = await api.get(`/qr/${uid}/analytics?days=${dateRange}&${tzParam}`);
      setQrAnalytics(data);
    } catch (e) {
      console.error('Error fetching QR analytics', e);
    }
  };

  const fetchSubData = async () => {
    try {
      const { data } = await api.get('/payments/subscription');
      if (data.success) setSubData(data);
    } catch (e) {
      console.error('Error fetching subscription data', e);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="body1" color="text.secondary">Loading analytics…</Typography>
      </Box>
    );
  }

  const isAdmin = user?.role === 'admin';

  const cardSelectRenderValue = (value) => {
    if (value === 'all') return isAdmin ? 'All Cards' : 'My Cards';
    if (String(value).startsWith('qr:')) {
      const uid = String(value).replace('qr:', '');
      const qr = qrCodes.find((q) => q.uid === uid);
      return qr ? `QR: ${qr.label || `${qr.uid.slice(0, 12)}...`}` : value;
    }
    return value;
  };
  const userPlan = subData?.plan || (user?.plan === 'premium' ? 'pro' : user?.plan) || 'free';
  const hasAnalyticsAccess = isAdmin || userPlan === 'pro' || userPlan === 'business' || userPlan === 'lifetime';

  const selectedCardHasAnalytics = () => hasAnalyticsAccess;

  const isFreePlan = !hasAnalyticsAccess;

  return (
    <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default', py: 4, px: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Insights into your digital business card performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedCard}
              onChange={(e) => setSelectedCard(e.target.value)}
              displayEmpty
              open={cardSelectOpen}
              onOpen={() => setCardSelectOpen(true)}
              onClose={() => {
                setCardSelectOpen(false);
                setCardSelectSearch('');
              }}
              renderValue={cardSelectRenderValue}
              MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 360 } },
              }}
              sx={{ borderRadius: 1, fontSize: '14px' }}
            >
              <ListSubheader
                sx={{ pt: 1, pb: 1, px: 1.5, lineHeight: 1 }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search cards…"
                  value={cardSelectSearch}
                  onChange={(e) => setCardSelectSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                  inputProps={{ autoComplete: 'off' }}
                />
              </ListSubheader>
              <MenuItem value="all">
                {isAdmin ? 'All Cards' : 'My Cards'}
              </MenuItem>
              {filteredCardsForSelect.map((card) => (
                <MenuItem key={card.id} value={card.cardUrl ?? card.card_url}>
                  {card.cardUrl ?? card.card_url}
                </MenuItem>
              ))}
              {qrCodes.length > 0 && filteredQrForSelect.length > 0 && (
                <MenuItem disabled sx={{ opacity: 0.6, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 1 }}>
                  — QR Codes —
                </MenuItem>
              )}
              {filteredQrForSelect.map((qr) => (
                <MenuItem key={qr.uid} value={`qr:${qr.uid}`}>
                  QR: {qr.label || `${qr.uid.slice(0, 12)}...`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              sx={{ borderRadius: 1, fontSize: '14px' }}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
              <MenuItem value={365}>Last year</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {isFreePlan && selectedCard === 'all' && <AnalyticsPaywall onUpgrade={() => navigate('/subscription')} />}

        <Box sx={isFreePlan && selectedCard === 'all' ? { filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' } : undefined}>
          {selectedCard === 'all' ? (
            <AllCardsAnalytics analytics={analytics} user={user} nfcAnalytics={nfcAnalytics} qrCodes={qrCodes} />
          ) : selectedCard.startsWith('qr:') ? (
            <QrScanAnalytics analytics={qrAnalytics} qrCode={qrCodes.find(q => q.uid === selectedCard.replace('qr:', ''))} user={user} />
          ) : !hasAnalyticsAccess ? (
            <CardAnalyticsPaywall cardUrl={selectedCard} onUpgrade={() => navigate('/subscription')} />
          ) : (
            <MarketingAnalytics
              analytics={marketingAnalytics}
              cardUrl={selectedCard}
              cardDisplayName={cardDisplayNameForSelected}
              onRefreshMarketing={() => fetchMarketingAnalytics(selectedCard)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const AnalyticsPaywall = ({ onUpgrade }) => (
  <Box sx={{
    position: 'absolute', inset: 0, zIndex: 10,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    pt: { xs: 4, md: 8 },
  }}>
    <Box sx={{
      bgcolor: '#fff', borderRadius: 2, boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      maxWidth: 960, width: '100%', mx: 2, overflow: 'hidden',
    }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, ${PRIMARY} 100%)`,
        px: { xs: 3, md: 5 }, py: 4, textAlign: 'center',
      }}>
        <Lock sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)', mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
          Unlock Analytics
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 400, mx: 'auto' }}>
          We're tracking your card and QR performance. Upgrade to see detailed insights about your reach and impact.
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 3, md: 4 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}>
          <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, border: '1px solid', borderColor: PRIMARY, bgcolor: `${PRIMARY}08` }}>
            <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700 }}>Pro Plan</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: ACCENT }}>₹499</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>/year + GST</Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '12px', color: 'text.secondary' }}>
              1 card + 5 QR codes + analytics + FREE NFC card
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, border: '1px solid', borderColor: '#8b5cf6', bgcolor: '#8b5cf608' }}>
            <Typography variant="overline" sx={{ color: '#8b5cf6', fontWeight: 700 }}>Business Plan</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: ACCENT }}>₹999</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>/year + GST</Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '12px', color: 'text.secondary' }}>
              5 cards + unlimited QR + analytics + 5 NFC cards
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, border: '1px solid', borderColor: '#ca8a04', bgcolor: '#fffbeb' }}>
            <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>Lifetime Access</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: ACCENT }}>₹2499</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>one-time + GST</Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: '12px', color: 'text.secondary' }}>
              1 card + 5 QR codes + analytics + FREE NFC card · pay once
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mb: 3 }}>
          {['Full analytics dashboard with real-time data', 'Location tracking — city & country', 'Device, browser & OS breakdown', 'Link click & traffic source tracking', 'Daily and weekly trend charts', 'Export analytics as CSV or PDF', 'FREE NFC visiting card included'].map(f => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
              <CheckCircleOutline sx={{ fontSize: 16, color: PRIMARY, mt: 0.25 }} />
              <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>{f}</Typography>
            </Box>
          ))}
        </Box>
        <Button fullWidth variant="contained" onClick={onUpgrade}
          sx={{ py: 1.5, bgcolor: PRIMARY, borderRadius: 1, fontWeight: 700, fontSize: '15px', textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' } }}>
          View Plans & Subscribe
        </Button>
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1.5, display: 'block', textAlign: 'center' }}>
          Secure payment via Razorpay · Cancel anytime
        </Typography>
      </Box>
    </Box>
  </Box>
);

const CardAnalyticsPaywall = ({ cardUrl, onUpgrade }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Lock sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
    <Typography variant="h5" fontWeight={700} gutterBottom>
      Analytics for {cardUrl}
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 1, maxWidth: 480, mx: 'auto' }}>
      Upgrade to Pro or Business plan to unlock detailed analytics — views, visitors, locations, devices, traffic sources and more.
    </Typography>
    <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 700, mb: 3 }}>
      Starting at ₹499/year + GST · Includes FREE NFC visiting card
    </Typography>
    <Button variant="contained" onClick={onUpgrade}
      sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0090c7' }, textTransform: 'none', borderRadius: 1.5, fontWeight: 700, px: 4, py: 1.25, boxShadow: 'none' }}>
      View Plans & Upgrade
    </Button>
  </Box>
);

const AllCardsAnalytics = ({ analytics, user, nfcAnalytics, qrCodes }) => {
  if (!analytics) return null;

  const totalViews = analytics.analytics?.reduce((sum, card) => sum + (card.total_views || 0), 0) || 0;
  const totalUniqueVisitors = analytics.analytics?.reduce((sum, card) => sum + (card.unique_visitors || 0), 0) || 0;
  const activeCards = analytics.analytics?.length || 0;
  const avgViewsPerCard = activeCards > 0 ? Math.round(totalViews / activeCards) : 0;

  if (totalViews === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Assessment sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Analytics Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 400, mx: 'auto' }}>
          Real analytics data will appear here once your cards start receiving visits.
          Data is collected from Google Analytics and updated daily.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
        gap: 2,
        mb: 4
      }}>
        {[
          { value: fmt(totalViews), label: 'Total Views', color: PRIMARY },
          { value: fmt(totalUniqueVisitors), label: 'Unique Visitors', color: ACCENT },
          { value: fmt(activeCards), label: 'Active Cards', color: '#F59E0B' },
          { value: fmt(avgViewsPerCard), label: 'Avg Views / Card', color: '#10B981' },
          { value: fmt(nfcAnalytics?.totalTaps || 0), label: 'NFC Taps (30d)', color: '#8B5CF6' },
          { value: fmt((qrCodes || []).reduce((s, q) => s + (q.scan_count || 0), 0)), label: 'QR Scans', color: '#EF4444' },
        ].map((kpi, i) => (
          <Card key={i} sx={{
            boxShadow: CARD_SHADOW,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: `4px solid ${kpi.color}`,
            bgcolor: `${kpi.color}0A`,
          }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {kpi.value}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>
                {kpi.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card sx={{ ...cardBase, overflow: 'hidden' }}>
        <Box sx={sectionHeaderSx}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Cards Performance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Overview of all your digital business cards
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Card URL</TableCell>
                <TableCell sx={tableHeadCellSx}>Template</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Views</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Unique Visitors</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.analytics?.map((card, index) => (
                <TableRow key={index} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ py: 2 }}>
                    <Typography
                      component="a"
                      href={`https://app.webcard.digital/${card.card_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ fontWeight: 500, color: PRIMARY, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {card.card_url}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Chip
                      label={card.template_name || 'Unknown'}
                      size="small"
                      sx={{
                        borderRadius: '12px',
                        fontSize: '12px',
                        height: 24,
                        bgcolor: `${PRIMARY}14`,
                        color: PRIMARY,
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2, fontWeight: 600, fontSize: '14px' }}>
                    {fmt(card.total_views || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2, fontWeight: 600, fontSize: '14px' }}>
                    {fmt(card.unique_visitors || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: card.is_public ? '#10B981' : '#9CA3AF' }} />
                      <Typography variant="body2" sx={{ fontSize: '13px', color: card.is_public ? '#10B981' : 'text.secondary' }}>
                        {card.is_public ? 'Public' : 'Private'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

const CAL_HOUR_PX = 52;
const TAP_BLOCK_H = 42;
const TAP_GAP = 4;

function formatHourLabel(h) {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

function calendarIpLabel(ipKey) {
  if (!ipKey || ipKey === '—') return 'Unknown IP';
  if (ipKey.length > 24) return `${ipKey.slice(0, 22)}…`;
  return ipKey;
}

const NotesTab = ({ cardUrl, refreshTrigger = 0 }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [totalTaps, setTotalTaps] = useState(0);
  const [totalTapsAllTime, setTotalTapsAllTime] = useState(0);
  const [logs, setLogs] = useState([]);
  const [startStr, setStartStr] = useState(() => addDaysYmd(toYmdLocal(new Date()), -29));
  const [endStr, setEndStr] = useState(() => toYmdLocal(new Date()));
  const [visibleDayStr, setVisibleDayStr] = useState(() => toYmdLocal(new Date()));
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedId, setSelectedId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  /** Calendar/list: one popover for pick (same IP) + edit — no side panel */
  const [notesPopover, setNotesPopover] = useState(null);

  const loadLogs = React.useCallback(async () => {
    if (!cardUrl) {
      setLoading(false);
      setLogs([]);
      setTotalTaps(0);
      setTotalTapsAllTime(0);
      return;
    }
    setLoading(true);
    setSaveError(null);
    let s = startStr;
    let e = endStr;
    if (s && e && s > e) {
      const t = s;
      s = e;
      e = t;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLogs([]);
        setTotalTaps(0);
        setTotalTapsAllTime(0);
        return;
      }
      const q = new URLSearchParams({ tz: String(-new Date().getTimezoneOffset()), from: s, to: e });
      const resp = await fetch(
        `https://api.webcard.digital/api/nfc/card-tap-logs/${encodeURIComponent(cardUrl)}?${q.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        setLogs([]);
        setTotalTaps(0);
        setTotalTapsAllTime(0);
        return;
      }
      const j = await resp.json();
      const arr = Array.isArray(j.logs) ? j.logs : [];
      setTotalTaps(j.totalTaps ?? 0);
      setTotalTapsAllTime(j.totalTapsAllTime ?? j.totalTaps ?? 0);
      setLogs(arr);
      // Show today when it falls in the selected range (not the day of the newest tap).
      const todayYmd = toYmdLocal(new Date());
      let dayToShow = e;
      if (todayYmd >= s && todayYmd <= e) dayToShow = todayYmd;
      else if (todayYmd < s) dayToShow = s;
      else dayToShow = e;
      setVisibleDayStr(dayToShow);
    } catch {
      setLogs([]);
      setTotalTaps(0);
      setTotalTapsAllTime(0);
    } finally {
      setLoading(false);
    }
  }, [cardUrl, startStr, endStr]);

  React.useEffect(() => {
    loadLogs();
  }, [loadLogs, refreshTrigger]);

  React.useEffect(() => {
    setVisibleDayStr((v) => {
      if (v < startStr) return startStr;
      if (v > endStr) return endStr;
      return v;
    });
  }, [startStr, endStr]);

  const selectedLog = selectedId != null ? logs.find((l) => l.id === selectedId) : null;

  React.useEffect(() => {
    if (selectedLog) {
      setEditTitle(selectedLog.title || '');
      setEditDescription(selectedLog.description || '');
    } else {
      setEditTitle('');
      setEditDescription('');
    }
  }, [selectedLog]);

  const closeNotesPopover = () => {
    setNotesPopover(null);
    setSelectedId(null);
  };

  const openSingleEdit = (anchor, log) => {
    setSaveError(null);
    setNotesPopover({
      anchor,
      step: 'edit',
      taps: undefined,
      country: log.country || '',
    });
    setSelectedId(log.id);
    setEditTitle(log.title || '');
    setEditDescription(log.description || '');
  };

  const openPickPopover = (anchor, taps, country, pickIpKey) => {
    setSaveError(null);
    setSelectedId(null);
    setNotesPopover({
      anchor,
      step: 'pick',
      taps,
      country: country || '',
      pickIpKey: pickIpKey || '',
    });
  };

  const goToEditFromPick = (log) => {
    setSelectedId(log.id);
    setEditTitle(log.title || '');
    setEditDescription(log.description || '');
    setSaveError(null);
    setNotesPopover((prev) => (prev ? { ...prev, step: 'edit' } : null));
  };

  const handleSave = async () => {
    if (selectedId == null) return;
    setSaving(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`https://api.webcard.digital/api/nfc/taps/${selectedId}/notes`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title: editTitle, description: editDescription }),
      });
      if (!resp.ok) {
        setSaveError('Could not save. Try again.');
        return;
      }
      setLogs((prev) =>
        prev.map((l) =>
          l.id === selectedId ? { ...l, title: editTitle, description: editDescription } : l
        )
      );
    } catch {
      setSaveError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const logsForVisibleDay = useMemo(() => {
    return logs
      .filter((l) => toYmdLocal(parseDate(l.created_at)) === visibleDayStr)
      .sort((a, b) => parseDate(a.created_at) - parseDate(b.created_at));
  }, [logs, visibleDayStr]);

  /** Calendar: one block per distinct IP per day (same IP → count); anchor time = latest tap in group. */
  const placedTaps = useMemo(() => {
    const ipGroupsMap = new Map();
    for (const log of logsForVisibleDay) {
      const rawIp = log.ip != null && String(log.ip).trim() !== '' ? String(log.ip).trim() : '';
      const ipKey = rawIp || '—';
      if (!ipGroupsMap.has(ipKey)) ipGroupsMap.set(ipKey, []);
      ipGroupsMap.get(ipKey).push(log);
    }

    const ipGroups = Array.from(ipGroupsMap.entries()).map(([ipKey, taps]) => {
      const sorted = [...taps].sort((a, b) => parseDate(a.created_at) - parseDate(b.created_at));
      return {
        ipKey,
        taps: sorted,
        anchorLog: sorted[sorted.length - 1],
        count: sorted.length,
      };
    });

    const byMinute = new Map();
    for (const g of ipGroups) {
      const d = parseDate(g.anchorLog.created_at);
      const k = d.getHours() * 60 + d.getMinutes();
      if (!byMinute.has(k)) byMinute.set(k, []);
      byMinute.get(k).push(g);
    }

    const preliminary = ipGroups.map((group) => {
      const d = parseDate(group.anchorLog.created_at);
      const k = d.getHours() * 60 + d.getMinutes();
      const minuteGroups = byMinute.get(k) || [group];
      const idx = minuteGroups.findIndex((x) => x.ipKey === group.ipKey);
      const n = minuteGroups.length;
      const minutes = d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
      const rawTop = (minutes / 60) * CAL_HOUR_PX;
      const wPct = 100 / n;
      const leftPct = idx * wPct;
      return { group, rawTop, idx, n, height: TAP_BLOCK_H, wPct, leftPct };
    });

    preliminary.sort((a, b) => {
      if (a.rawTop !== b.rawTop) return a.rawTop - b.rawTop;
      return a.leftPct - b.leftPct;
    });

    function rectsOverlap(topA, hA, leftA, wA, topB, hB, leftB, wB) {
      const h = !(leftA + wA <= leftB || leftB + wB <= leftA);
      const v = !(topA + hA <= topB || topB + hB <= topA);
      return h && v;
    }

    const placed = [];
    for (const p of preliminary) {
      let top = p.rawTop;
      for (let guard = 0; guard < 400; guard++) {
        let bumped = false;
        for (const q of placed) {
          if (
            rectsOverlap(top, p.height, p.leftPct, p.wPct, q.top, q.height, q.leftPct, q.wPct)
          ) {
            top = Math.max(top, q.top + q.height + TAP_GAP);
            bumped = true;
          }
        }
        if (!bumped) break;
      }
      placed.push({ ...p, top });
    }

    return placed;
  }, [logsForVisibleDay]);

  const calendarContentHeight = useMemo(() => {
    const base = 24 * CAL_HOUR_PX;
    if (!placedTaps.length) return base;
    const bottom = Math.max(...placedTaps.map((p) => p.top + p.height));
    return Math.max(base, bottom + 20);
  }, [placedTaps]);

  const isVisibleToday = visibleDayStr === toYmdLocal(new Date());
  const nowLineTop = useMemo(() => {
    if (!isVisibleToday) return null;
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
    return (minutes / 60) * CAL_HOUR_PX;
  }, [isVisibleToday]);

  const goPrevDay = () => setVisibleDayStr((d) => (d <= startStr ? d : addDaysYmd(d, -1)));
  const goNextDay = () => setVisibleDayStr((d) => (d >= endStr ? d : addDaysYmd(d, 1)));
  const setThisWeek = () => {
    const t = toYmdLocal(new Date());
    setEndStr(t);
    setStartStr(addDaysYmd(t, -6));
  };
  const setLast30 = () => {
    const t = toYmdLocal(new Date());
    setEndStr(t);
    setStartStr(addDaysYmd(t, -29));
  };

  if (loading && !logs.length && totalTapsAllTime === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="body1" color="text.secondary">
          Loading tap logs…
        </Typography>
      </Box>
    );
  }

  const noNfcEver = !loading && totalTapsAllTime === 0 && !logs.length;
  if (noNfcEver) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <StickyNote2 sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No tap logs yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 440, mx: 'auto' }}>
          When someone taps an NFC tag linked to this card, each tap appears on the calendar by time. You can filter a date range and add private notes to any tap.
        </Typography>
      </Box>
    );
  }

  const rangeSummary =
    totalTapsAllTime > totalTaps
      ? `${fmt(totalTaps)} tap${totalTaps === 1 ? '' : 's'} · ${fmt(totalTapsAllTime)} all time`
      : `${fmt(totalTaps)} tap${totalTaps === 1 ? '' : 's'}`;

  const calendarGrid = (
    <Card sx={{ ...cardBase, overflow: 'hidden' }}>
      <Box sx={{ ...sectionHeaderSx, py: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={goPrevDay} disabled={visibleDayStr <= startStr} aria-label="Previous day">
              <ChevronLeft />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, px: 1 }}>
              <CalendarMonth sx={{ color: PRIMARY, fontSize: 22 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {fmtDate(parseDate(visibleDayStr), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
            <IconButton size="small" onClick={goNextDay} disabled={visibleDayStr >= endStr} aria-label="Next day">
              <ChevronRight />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Times in your local timezone
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: 'relative', maxHeight: 'min(70vh, 640px)', overflow: 'auto' }}>
        <Box sx={{ display: 'flex', pl: 1, pr: 1, pb: 2 }}>
          <Box sx={{ width: 52, flexShrink: 0, position: 'relative', minHeight: calendarContentHeight }}>
            {Array.from({ length: 24 }, (_, h) => (
              <Typography
                key={h}
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: h * CAL_HOUR_PX - 6,
                  right: 6,
                  color: 'text.secondary',
                  fontSize: '11px',
                  lineHeight: 1,
                }}
              >
                {formatHourLabel(h)}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              minHeight: calendarContentHeight,
              borderLeft: '1px solid',
              borderColor: 'divider',
              borderBottom: '1px solid',
              borderBottomColor: 'divider',
            }}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <Box
                key={h}
                sx={{
                  height: CAL_HOUR_PX,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: h % 2 === 0 ? 'action.hover' : 'transparent',
                  opacity: h % 2 === 0 ? 0.35 : 1,
                }}
              />
            ))}
            {calendarContentHeight > 24 * CAL_HOUR_PX && (
              <Box
                sx={{
                  minHeight: calendarContentHeight - 24 * CAL_HOUR_PX,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  opacity: 0.2,
                }}
              />
            )}
            {nowLineTop != null && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: nowLineTop,
                  height: 2,
                  bgcolor: '#ea4335',
                  zIndex: 3,
                  pointerEvents: 'none',
                  boxShadow: '0 0 0 1px rgba(234,67,53,0.25)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: -4,
                    top: -4,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: '#ea4335',
                  }}
                />
              </Box>
            )}
            {placedTaps.map(({ group, top, height, wPct, leftPct }) => {
              const { ipKey, taps, anchorLog, count } = group;
              const isMulti = count > 1;
              const titleTrim = (anchorLog.title || '').trim();
              const descTrim = (anchorLog.description || '').trim();
              const hasNote = taps.some(
                (t) => (t.title || '').trim() || (t.description || '').trim()
              );
              const timeLine = isMulti
                ? `${count} taps`
                : parseDate(anchorLog.created_at).toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                  });
              const secondLine = isMulti
                ? calendarIpLabel(ipKey)
                : titleTrim ||
                  (descTrim
                    ? descTrim.length > 40
                      ? `${descTrim.slice(0, 40)}…`
                      : descTrim
                    : anchorLog.country || calendarIpLabel(ipKey));
              const isSelected =
                (notesPopover?.step === 'edit' && taps.some((t) => t.id === selectedId)) ||
                (notesPopover?.step === 'pick' && notesPopover.pickIpKey === ipKey);

              return (
                <Box
                  key={ipKey}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isMulti) openPickPopover(e.currentTarget, group.taps, anchorLog.country, ipKey);
                    else openSingleEdit(e.currentTarget, anchorLog);
                  }}
                  sx={{
                    position: 'absolute',
                    left: `calc(${leftPct}% + 4px)`,
                    width: `calc(${wPct}% - 8px)`,
                    top,
                    height,
                    zIndex: 2,
                    px: 0.75,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: isSelected ? PRIMARY : 'divider',
                    bgcolor: isSelected ? `${PRIMARY}22` : hasNote ? `${ACCENT}14` : `${PRIMARY}12`,
                    boxShadow: isSelected ? `0 0 0 2px ${PRIMARY}44` : CARD_SHADOW,
                    transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.12s',
                    '&:hover': {
                      borderColor: PRIMARY,
                      boxShadow: `0 2px 8px rgba(0,0,0,0.12)`,
                      zIndex: 4,
                    },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2, color: ACCENT }}>
                    {timeLine}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    title={secondLine}
                    sx={{
                      display: 'block',
                      lineHeight: 1.25,
                      color: isMulti || titleTrim ? 'text.primary' : 'text.secondary',
                      fontSize: '11px',
                      fontWeight: isMulti || titleTrim ? 600 : 400,
                      mt: 0.25,
                    }}
                  >
                    {secondLine}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        {!logsForVisibleDay.length && (
          <Box sx={{ px: 3, pb: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No taps on this day in the selected range. Try another day or widen the date range.
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );

  const listView = (
    <Card sx={cardBase}>
      <Box sx={sectionHeaderSx}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Tap list
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {rangeSummary}
        </Typography>
      </Box>
      <List disablePadding sx={{ maxHeight: { xs: 360, md: 480 }, overflow: 'auto' }}>
        {logs.map((row, index) => {
          const hasNote = Boolean((row.title && row.title.trim()) || (row.description && row.description.trim()));
          return (
            <React.Fragment key={row.id}>
              {index > 0 && <Divider component="li" />}
              <ListItemButton
                selected={selectedId === row.id && notesPopover?.step === 'edit'}
                onClick={(e) => {
                  e.stopPropagation();
                  openSingleEdit(e.currentTarget, row);
                }}
                alignItems="flex-start"
                sx={{
                  py: 1.75,
                  px: 3,
                  '&.Mui-selected': {
                    bgcolor: `${PRIMARY}14`,
                    borderLeft: `3px solid ${PRIMARY}`,
                    pl: 2.75,
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {fmtDateTime(row.created_at)}
                      </Typography>
                      {row.country ? (
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                          · {row.country}
                        </Typography>
                      ) : null}
                      {row.ip ? (
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                          · {row.ip}
                        </Typography>
                      ) : null}
                      {hasNote && (
                        <Chip label="Note" size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600 }} />
                      )}
                    </Box>
                  }
                  secondary={
                    hasNote && row.title ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {row.title}
                      </Typography>
                    ) : null
                  }
                />
              </ListItemButton>
            </React.Fragment>
          );
        })}
      </List>
    </Card>
  );

  const toolbar = (
    <Card sx={{ ...cardBase, mb: 2 }}>
      <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            type="date"
            label="From"
            size="small"
            value={startStr}
            onChange={(e) => setStartStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 160 } }}
          />
          <TextField
            type="date"
            label="To"
            size="small"
            value={endStr}
            onChange={(e) => setEndStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 160 } }}
          />
          <Button size="small" variant="outlined" onClick={setThisWeek} sx={{ textTransform: 'none' }}>
            Last 7 days
          </Button>
          <Button size="small" variant="outlined" onClick={setLast30} sx={{ textTransform: 'none' }}>
            Last 30 days
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(e, v) => v && setViewMode(v)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
          >
            <ToggleButton value="calendar" aria-label="Calendar view">
              <CalendarMonth sx={{ fontSize: 18, mr: 0.75 }} />
              Calendar
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              <ViewList sx={{ fontSize: 18, mr: 0.75 }} />
              List
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {rangeSummary}
        </Typography>
      </CardContent>
    </Card>
  );

  const popoverEditLog = notesPopover?.step === 'edit' && selectedId != null ? logs.find((l) => l.id === selectedId) : null;

  return (
    <Box sx={{ width: '100%' }}>
      {toolbar}
      <Box sx={{ minWidth: 0 }}>{viewMode === 'calendar' ? calendarGrid : listView}</Box>
      <Popover
        open={Boolean(notesPopover)}
        anchorEl={notesPopover?.anchor ?? null}
        onClose={closeNotesPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'hidden',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.primary',
              maxWidth: 400,
              boxShadow: theme.shadows[8],
            },
          },
        }}
      >
        {notesPopover?.step === 'pick' && notesPopover.taps ? (
          <Box sx={{ py: 0.5, minWidth: 280, maxWidth: 360, maxHeight: 420, overflowY: 'auto' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, px: 2, pt: 1.5, pb: 1, color: 'text.primary' }}>
              {notesPopover.taps.length} tap{notesPopover.taps.length !== 1 ? 's' : ''}
              {notesPopover.country ? ` · ${notesPopover.country}` : ''}
            </Typography>
            <List dense disablePadding>
              {notesPopover.taps.map((log) => {
                const tt = (log.title || '').trim();
                const dd = (log.description || '').trim();
                const sub = tt || (dd ? (dd.length > 72 ? `${dd.slice(0, 72)}…` : dd) : 'No note');
                return (
                  <ListItemButton
                    key={log.id}
                    onClick={() => goToEditFromPick(log)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemText
                      primary={parseDate(log.created_at).toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                      secondary={sub}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                      secondaryTypographyProps={{
                        variant: 'body2',
                        sx: { color: 'text.secondary', whiteSpace: 'normal' },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ) : null}
        {notesPopover?.step === 'edit' && popoverEditLog ? (
          <Box sx={{ p: 2, color: 'text.primary' }}>
            {notesPopover.taps && notesPopover.taps.length > 1 ? (
              <Button
                size="small"
                color="primary"
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                onClick={() => {
                  setSaveError(null);
                  setNotesPopover((prev) => (prev ? { ...prev, step: 'pick' } : null));
                  setSelectedId(null);
                }}
                sx={{ mb: 1.5, textTransform: 'none' }}
              >
                All taps
              </Button>
            ) : null}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
              Note for this tap
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {fmtDateTime(popoverEditLog.created_at)}
              {popoverEditLog.country ? ` · ${popoverEditLog.country}` : ''}
              {popoverEditLog.ip ? ` · ${popoverEditLog.ip}` : ''}
            </Typography>
            <TextField
              fullWidth
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 512 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              multiline
              minRows={4}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 20000 }}
            />
            {saveError ? (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {saveError}
              </Typography>
            ) : null}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: PRIMARY, '&:hover': { bgcolor: '#0090c7' } }}
            >
              {saving ? 'Saving…' : 'Save note'}
            </Button>
          </Box>
        ) : null}
      </Popover>
    </Box>
  );
};

const MarketingAnalytics = ({ analytics, cardUrl, cardDisplayName, onRefreshMarketing }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [tapAnalytics, setTapAnalytics] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [notesRefreshTrigger, setNotesRefreshTrigger] = useState(0);

  const fetchTapAnalytics = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !cardUrl) return;
      const resp = await fetch(`https://api.webcard.digital/api/nfc/card-analytics/${encodeURIComponent(cardUrl)}?${tzParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const j = await resp.json();
        setTapAnalytics(j);
      }
    } catch (e) {
      // silent
    }
  }, [cardUrl]);

  React.useEffect(() => {
    fetchTapAnalytics();
  }, [fetchTapAnalytics]);

  const handleRefreshAll = React.useCallback(async () => {
    if (!onRefreshMarketing) return;
    setRefreshingAll(true);
    try {
      await Promise.resolve(onRefreshMarketing());
      await fetchTapAnalytics();
      setNotesRefreshTrigger((t) => t + 1);
    } finally {
      setRefreshingAll(false);
    }
  }, [onRefreshMarketing, fetchTapAnalytics]);

  const tabSections = [
    { label: 'Overview', icon: <Visibility /> },
    { label: 'Notes', icon: <StickyNote2 /> },
    { label: 'Devices', icon: <Devices /> },
    { label: 'Locations', icon: <LocationOn /> },
    { label: 'Traffic Sources', icon: <Share /> },
    { label: 'Time Analysis', icon: <Schedule /> },
    { label: 'Tap Analysis', icon: <QrCode /> },
  ];

  const exportCSV = () => {
    setExportAnchor(null);
    if (!analytics) return;
    const rows = [['Date', 'Views', 'Unique Visitors']];
    (analytics.viewsOverTime || []).forEach(d => rows.push([fmtDate(d.date), d.views, d.unique_visitors]));

    if (analytics.deviceAnalytics?.length) {
      rows.push([], ['--- Device Analytics ---'], ['Device Type', 'Browser', 'OS', 'Views']);
      analytics.deviceAnalytics.forEach(d => rows.push([d.device_type, d.browser, d.os, d.total_views]));
    }
    if (analytics.geographicAnalytics?.length) {
      rows.push([], ['--- Geographic Analytics ---'], ['Country', 'City', 'Views', 'Unique Visitors']);
      analytics.geographicAnalytics.forEach(g => rows.push([g.country, g.city, g.total_views, g.unique_visitors]));
    }
    if (analytics.trafficSources?.length) {
      rows.push([], ['--- Traffic Sources ---'], ['Source', 'Medium', 'Views']);
      analytics.trafficSources.forEach(t => rows.push([t.source, t.medium, t.total_views]));
    }
    if (analytics.timeAnalytics?.hourly?.length) {
      rows.push([], ['--- Hourly Breakdown ---'], ['Hour', 'Views']);
      analytics.timeAnalytics.hourly.forEach(h => rows.push([h.hour, h.views]));
    }

    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `analytics-${cardUrl}-${fmtDateFile(new Date())}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    setExportAnchor(null);
    downloadMarketingAnalyticsPdf(analytics, cardUrl, cardDisplayName);
  };

  if (!analytics) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ ...cardBase, mb: 3 }}>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 1,
              bgcolor: `${PRIMARY}14`,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <QrCode sx={{ fontSize: 24, color: PRIMARY }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {cardUrl}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Digital Business Card • {analytics.period}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            <Button
              variant="outlined" size="small"
              onClick={handleRefreshAll}
              disabled={refreshingAll}
              startIcon={refreshingAll ? <CircularProgress size={14} thickness={5} /> : <Refresh sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500 }}
            >
              {refreshingAll ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button
              variant="outlined" size="small"
              startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`https://app.webcard.digital/${cardUrl}`, '_blank')}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500 }}
            >
              View Card
            </Button>
            <Button
              variant="contained" size="small"
              startIcon={<FileDownload sx={{ fontSize: 16 }} />}
              onClick={(e) => setExportAnchor(e.currentTarget)}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500 }}
            >
              Export Data
            </Button>
            <Menu
              anchorEl={exportAnchor} open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { borderRadius: 1, minWidth: 160, mt: 0.5 } }}
            >
              <MenuItem onClick={exportCSV} sx={{ fontSize: '14px', gap: 1.5 }}>
                <FileDownload sx={{ fontSize: 18, color: '#10B981' }} /> Export as CSV
              </MenuItem>
              <MenuItem onClick={exportPDF} sx={{ fontSize: '14px', gap: 1.5 }}>
                <FileDownload sx={{ fontSize: 18, color: '#EF4444' }} /> Export as PDF
              </MenuItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          minHeight: 42,
          '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            minHeight: 42,
            px: 2,
            mr: 1,
          },
        }}
      >
        {tabSections.map((section, index) => (
          <Tab
            key={index}
            label={section.label}
            icon={section.icon}
            iconPosition="start"
            sx={{ minWidth: 'auto' }}
          />
        ))}
      </Tabs>

      {activeTab === 0 && <OverviewTab analytics={analytics} />}
      {activeTab === 1 && <NotesTab cardUrl={cardUrl} refreshTrigger={notesRefreshTrigger} />}
      {activeTab === 2 && <DevicesTab analytics={analytics} />}
      {activeTab === 3 && <LocationsTab analytics={analytics} />}
      {activeTab === 4 && <TrafficSourcesTab analytics={analytics} />}
      {activeTab === 5 && <TimeAnalysisTab analytics={analytics} />}
      {activeTab === 6 && <TapAnalysisTab data={tapAnalytics} />}
    </Box>
  );
};

const OverviewTab = ({ analytics }) => {
  const theme = useTheme();

  if (!analytics.viewsOverTime || analytics.viewsOverTime.length === 0 ||
      analytics.viewsOverTime.every(day => day.views === 0)) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Analytics Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 400, mx: 'auto' }}>
          Real analytics data will appear here once this card starts receiving visits.
          Data is collected from Google Analytics and updated daily.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
        gap: 2,
        mb: 4
      }}>
        {[
          { value: fmt(analytics.totalStats?.total_views || 0), label: 'Total Views', color: PRIMARY },
          { value: fmt(analytics.totalStats?.unique_visitors || 0), label: 'Unique Visitors', color: ACCENT },
          { value: fmt(analytics.totalStats?.active_days || 0), label: 'Active Days', color: '#F59E0B' },
          { value: `${analytics.totalStats?.avg_view_duration || '0.0'}s`, label: 'Avg Duration', color: '#10B981' },
        ].map((kpi, i) => (
          <Card key={i} sx={{
            boxShadow: CARD_SHADOW,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: `4px solid ${kpi.color}`,
            bgcolor: `${kpi.color}0A`,
          }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {kpi.value}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>
                {kpi.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card sx={cardBase}>
        <Box sx={sectionHeaderSx}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Views Over Time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track your card's performance over the selected period
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={analytics.viewsOverTime || []}>
              <defs>
                <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={PRIMARY} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="visitorsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ACCENT} stopOpacity={0.15} />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => fmtDateShort(value)}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <RechartsTooltip
                labelFormatter={(value) => fmtDate(value)}
                formatter={(value, name) => [value, name === 'views' ? 'Views' : 'Unique Visitors']}
              />
              <Area type="monotone" dataKey="views" stroke={PRIMARY} fill="url(#viewsGradient)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="unique_visitors" stroke={ACCENT} fill="url(#visitorsGradient)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

const DevicesTab = ({ analytics }) => {
  const theme = useTheme();
  if (!analytics?.deviceAnalytics || analytics.deviceAnalytics.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Devices sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Device Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 400, mx: 'auto' }}>
          Device analytics will appear here once this card starts receiving visits.
        </Typography>
      </Box>
    );
  }

  let deviceTypes = analytics.deviceAnalytics || [];
  let totalViews = deviceTypes.reduce((sum, device) => sum + parseInt(device.total_views || 0), 0);
  
  if (deviceTypes.length === 0 || totalViews === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Devices sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Device Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 400, mx: 'auto' }}>
          Device analytics will appear here once this card starts receiving visits.
        </Typography>
      </Box>
    );
  }
  
  // Create unique browser data by grouping
  const browserData = deviceTypes.reduce((acc, device) => {
    const browser = device.browser || 'Unknown';
    if (acc[browser]) {
      acc[browser] += parseInt(device.total_views || 0);
    } else {
      acc[browser] = parseInt(device.total_views || 0);
    }
    return acc;
  }, {});

  const browserList = Object.entries(browserData).map(([browser, views]) => ({
    browser,
    views,
    percentage: totalViews > 0 ? Math.round((views / totalViews) * 100) : 0
  })).sort((a, b) => b.views - a.views);

  // Create device type summary for KPI cards - REAL DATA ONLY
  const deviceSummary = deviceTypes.reduce((acc, device) => {
    const type = device.device_type || 'Unknown';
    if (acc[type]) {
      acc[type] += parseInt(device.total_views || 0);
    } else {
      acc[type] = parseInt(device.total_views || 0);
    }
    return acc;
  }, {});

  const deviceList = Object.entries(deviceSummary).map(([type, views]) => ({
    type,
    views,
    percentage: totalViews > 0 ? Math.round((views / totalViews) * 100) : 0
  })).sort((a, b) => b.views - a.views);

  // Get specific device counts for KPI cards - REAL DATA ONLY
  const desktopViews = deviceList.find(d => d.type.toLowerCase().includes('desktop'))?.views || 0;
  const mobileViews = deviceList.find(d => d.type.toLowerCase().includes('mobile'))?.views || 0;
  const tabletViews = deviceList.find(d => d.type.toLowerCase().includes('tablet'))?.views || 0;
  const topBrowserViews = browserList.length > 0 ? browserList[0]?.views || 0 : 0;

  const DEVICE_COLORS = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.warning.main, '#8B5CF6'];
  const pieData = deviceTypes.map((d) => ({
    name: d.device_type,
    value: parseInt(d.total_views || 0),
  }));

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 3,
        mb: 4
      }}>
        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Device Metrics
            </Typography>
          </Box>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {[
                { icon: <Monitor sx={{ fontSize: 20, color: PRIMARY }} />, value: fmt(desktopViews), label: 'Desktop', pct: desktopViews > 0 ? `${Math.round((desktopViews / totalViews) * 100)}% of total` : 'No data', color: PRIMARY },
                { icon: <PhoneAndroid sx={{ fontSize: 20, color: ACCENT }} />, value: fmt(mobileViews), label: 'Mobile', pct: mobileViews > 0 ? `${Math.round((mobileViews / totalViews) * 100)}% of total` : 'No data', color: ACCENT },
                { icon: <Tablet sx={{ fontSize: 20, color: '#F59E0B' }} />, value: fmt(tabletViews), label: 'Tablet', pct: tabletViews > 0 ? `${Math.round((tabletViews / totalViews) * 100)}% of total` : 'No data', color: '#F59E0B' },
                { icon: <Computer sx={{ fontSize: 20, color: '#10B981' }} />, value: fmt(topBrowserViews), label: browserList[0]?.browser || 'Browser', pct: 'Top browser', color: '#10B981' },
              ].map((m, i) => (
                <Box key={i} sx={{
                  p: 2, borderRadius: 1,
                  border: '1px solid', borderColor: 'divider',
                  borderLeft: `4px solid ${m.color}`,
                  bgcolor: `${m.color}06`,
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.25 }}>
                    {m.value}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '12px', textTransform: 'uppercase', mb: 0.25 }}>
                    {m.label}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">{m.pct}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Device Distribution
            </Typography>
          </Box>
          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((_, index) => (
                    <Cell key={index} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value, name) => [value, name]} />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
              {deviceTypes.map((device, index) => {
                const percentage = Math.round((parseInt(device.total_views) / totalViews) * 100);
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: DEVICE_COLORS[index % DEVICE_COLORS.length] }} />
                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      {device.device_type} ({percentage}%)
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const LocationsTab = ({ analytics }) => {
  if (!analytics?.geographicAnalytics || analytics.geographicAnalytics.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <LocationOn sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Location Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 400, mx: 'auto' }}>
          Geographic analytics will appear here once this card starts receiving visits.
        </Typography>
      </Box>
    );
  }

  const geoData = analytics.geographicAnalytics || [];
  const totalViews = geoData.reduce((sum, location) => sum + parseInt(location.total_views || 0), 0);

  // Group data by country for better analytics
  const countryData = geoData.reduce((acc, location) => {
    const country = location.country;
    if (!country) return acc; // Skip if no country data
    
    if (!acc[country]) {
      acc[country] = {
        country: country,
        total_views: 0,
        cities: []
      };
    }
    acc[country].total_views += parseInt(location.total_views || 0);
    acc[country].cities.push({
      city: location.city || 'Unknown',
      views: parseInt(location.total_views || 0)
    });
    return acc;
  }, {});

  const countryArray = Object.values(countryData).sort((a, b) => b.total_views - a.total_views);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3,
        mb: 4
      }}>
        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Active Users by Country
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{
              position: 'relative', height: '350px', borderRadius: 1,
              border: '1px solid', borderColor: 'divider', overflow: 'hidden'
            }}>
              <WorldMap countryData={countryArray} width={800} height={350} />
              <Box sx={{
                position: 'absolute', top: 16, right: 16,
                bgcolor: 'background.paper', borderRadius: 1, p: 1.5,
                boxShadow: CARD_SHADOW, border: '1px solid', borderColor: 'divider'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY, mb: 0.25 }}>
                  {countryArray.length > 0 ? fmt(countryArray[0].total_views) : 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {countryArray.length > 0 ? countryArray[0].country : 'No data'}
                </Typography>
              </Box>
              <Box sx={{
                position: 'absolute', bottom: 16, left: 16,
                bgcolor: 'background.paper', borderRadius: 1, p: 1.5,
                boxShadow: CARD_SHADOW, border: '1px solid', borderColor: 'divider'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 0.75 }}>
                  Performance Levels
                </Typography>
                {[
                  { color: 'primary.main', label: 'Top (80%+)' },
                  { color: 'secondary.main', label: 'Good (50%+)' },
                  { color: 'warning.main', label: 'Moderate (20%+)' },
                  { color: 'success.main', label: 'Low' },
                  { color: 'grey.300', label: 'No data' },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                    <Box sx={{ width: 10, height: 6, bgcolor: item.color, borderRadius: '2px' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Top Countries
            </Typography>
          </Box>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Country</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>Users</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {countryArray.slice(0, 8).map((country, index) => {
                    const percentage = Math.round((country.total_views / totalViews) * 100);
                    const isTop = index === 0;
                    return (
                      <TableRow key={index} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isTop ? PRIMARY : 'text.disabled' }} />
                            <Typography variant="body2" sx={{ fontWeight: isTop ? 600 : 400, color: isTop ? PRIMARY : 'text.primary' }}>
                              {country.country}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography component="span" variant="body2" sx={{ fontWeight: isTop ? 700 : 500 }}>
                            {fmt(country.total_views)}
                          </Typography>
                          <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>
                            ({percentage}%)
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const TrafficSourcesTab = ({ analytics }) => {
  const theme = useTheme();
  if (!analytics?.referrerAnalytics || analytics.referrerAnalytics.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Share sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Traffic Source Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 420, mx: 'auto' }}>
          Traffic source analytics will appear here once visitors start coming to your card from different sources like social media, search engines, or direct links.
        </Typography>
      </Box>
    );
  }

  const referrerData = analytics.referrerAnalytics || [];
  const totalViews = referrerData.reduce((sum, referrer) => sum + parseInt(referrer.total_views || 0), 0);
  
  // Calculate metrics
  const directTraffic = referrerData.find(r => r.referrer_type === 'direct')?.total_views || 0;
  const organicSearch = referrerData.find(r => r.referrer_type === 'organic')?.total_views || 0;
  const socialMedia = referrerData.find(r => r.referrer_type === 'social')?.total_views || 0;
  const referral = referrerData.find(r => r.referrer_type === 'referral')?.total_views || 0;
  const email = referrerData.find(r => r.referrer_type === 'email')?.total_views || 0;
  const other = referrerData.find(r => r.referrer_type === 'other')?.total_views || 0;

  // Create comprehensive chart data with all traffic source types
  const chartData = [
    { source: 'Direct', views: directTraffic, color: 'primary.main' },
    { source: 'Organic Search', views: organicSearch, color: 'success.main' },
    { source: 'Social Media', views: socialMedia, color: 'error.main' },
    { source: 'Referral', views: referral, color: 'warning.main' },
    { source: 'Email', views: email, color: 'secondary.main' },
    { source: 'Other', views: other, color: 'grey.600' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: 2, mb: 4
      }}>
        {[
          { value: fmt(directTraffic), label: 'Direct Traffic', color: PRIMARY, pct: totalViews > 0 ? Math.round((directTraffic / totalViews) * 100) : 0 },
          { value: fmt(organicSearch), label: 'Organic Search', color: '#10B981', pct: totalViews > 0 ? Math.round((organicSearch / totalViews) * 100) : 0 },
          { value: fmt(socialMedia), label: 'Social Media', color: '#EF4444', pct: totalViews > 0 ? Math.round((socialMedia / totalViews) * 100) : 0 },
          { value: fmt(referral), label: 'Referral', color: '#F59E0B', pct: totalViews > 0 ? Math.round((referral / totalViews) * 100) : 0 },
        ].map((kpi, i) => (
          <Card key={i} sx={{
            boxShadow: CARD_SHADOW,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: `4px solid ${kpi.color}`,
            bgcolor: `${kpi.color}0A`,
          }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {kpi.value}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>
                {kpi.label}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {kpi.pct}% of total
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3
      }}>
        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Traffic Sources Distribution
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="source" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip formatter={(value) => [value, 'Views']} />
                <Bar dataKey="views" fill={theme.palette.primary.main} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Top Referrers
            </Typography>
          </Box>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Source</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>Views</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referrerData.slice(0, 8).map((referrer, index) => {
                    const percentage = Math.round((parseInt(referrer.total_views) / totalViews) * 100);
                    const isTop = index === 0;
                    return (
                      <TableRow key={index} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isTop ? PRIMARY : 'text.disabled' }} />
                            <Typography variant="body2" sx={{ fontWeight: isTop ? 600 : 400, color: isTop ? PRIMARY : 'text.primary' }}>
                              {referrer.referrer_domain || referrer.referrer_type}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: isTop ? 700 : 500 }}>
                            {referrer.total_views}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: isTop ? 700 : 500, color: isTop ? PRIMARY : 'text.primary' }}>
                            {percentage}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const TimeAnalysisTab = ({ analytics }) => {
  const theme = useTheme();
  if (!analytics?.hourlyDistribution || analytics.hourlyDistribution.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Schedule sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Time Analysis Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 420, mx: 'auto' }}>
          Time analysis will show when your card receives visits throughout different hours of the day.
        </Typography>
      </Box>
    );
  }

  const hourlyData = analytics.hourlyDistribution || [];
  const totalViews = hourlyData.reduce((sum, hour) => sum + parseInt(hour.views || 0), 0);
  
  // Calculate peak hours and metrics
  const peakHour = hourlyData.reduce((max, hour) => 
    parseInt(hour.views || 0) > parseInt(max.views || 0) ? hour : max, hourlyData[0] || {});
  
  const morningViews = hourlyData.slice(6, 12).reduce((sum, hour) => sum + parseInt(hour.views || 0), 0);
  const afternoonViews = hourlyData.slice(12, 18).reduce((sum, hour) => sum + parseInt(hour.views || 0), 0);
  const eveningViews = hourlyData.slice(18, 24).reduce((sum, hour) => sum + parseInt(hour.views || 0), 0);
  const nightViews = hourlyData.slice(0, 6).reduce((sum, hour) => sum + parseInt(hour.views || 0), 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: 2, mb: 4
      }}>
        {[
          { value: `${peakHour.hour || 0}:00`, label: 'Peak Hour', sub: `${peakHour.views || 0} views`, color: PRIMARY },
          { value: afternoonViews, label: 'Afternoon (12–18h)', sub: `${Math.round((afternoonViews / totalViews) * 100)}%`, color: '#10B981' },
          { value: eveningViews, label: 'Evening (18–24h)', sub: `${Math.round((eveningViews / totalViews) * 100)}%`, color: '#EF4444' },
          { value: morningViews, label: 'Morning (6–12h)', sub: `${Math.round((morningViews / totalViews) * 100)}%`, color: '#F59E0B' },
        ].map((kpi, i) => (
          <Card key={i} sx={{
            boxShadow: CARD_SHADOW,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: `4px solid ${kpi.color}`,
            bgcolor: `${kpi.color}0A`,
          }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {kpi.value}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>
                {kpi.label}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {kpi.sub}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3
      }}>
        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Hourly Distribution
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="hour" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}:00`} />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip labelFormatter={(v) => `${v}:00`} formatter={(value) => [value, 'Views']} />
                <Bar dataKey="views" fill={theme.palette.primary.main} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Time Periods
            </Typography>
          </Box>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Period</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>Views</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { period: 'Morning (6–12h)', views: morningViews, color: '#F59E0B' },
                    { period: 'Afternoon (12–18h)', views: afternoonViews, color: '#10B981' },
                    { period: 'Evening (18–24h)', views: eveningViews, color: '#EF4444' },
                    { period: 'Night (0–6h)', views: nightViews, color: ACCENT },
                  ].map((tp, index) => {
                    const percentage = Math.round((tp.views / totalViews) * 100);
                    const isTop = tp.views === Math.max(morningViews, afternoonViews, eveningViews, nightViews);
                    return (
                      <TableRow key={index} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tp.color }} />
                            <Typography variant="body2" sx={{ fontWeight: isTop ? 600 : 400, color: isTop ? PRIMARY : 'text.primary' }}>
                              {tp.period}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: isTop ? 700 : 500 }}>
                            {tp.views}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: isTop ? 700 : 500, color: isTop ? PRIMARY : 'text.primary' }}>
                            {percentage}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const TapAnalysisTab = ({ data }) => {
  const theme = useTheme();
  if (!data || !data.hourlyDistribution || data.hourlyDistribution.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <QrCode sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
          No Tap Data Yet
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 420, mx: 'auto' }}>
          When users tap your NFC tag, hourly distributions and totals will appear here.
        </Typography>
      </Box>
    );
  }

  const hourlyData = Array.from({ length: 24 }, (_, h) => ({ hour: h, taps: 0 }));
  data.hourlyDistribution.forEach(r => { if (hourlyData[r.hour]) hourlyData[r.hour].taps = parseInt(r.taps || 0); else hourlyData[r.hour] = { hour: r.hour, taps: parseInt(r.taps || 0) }; });
  const total = data.totalTaps || 0;
  const peak = hourlyData.reduce((max, h) => h.taps > max.taps ? h : max, { hour: 0, taps: 0 });

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
        <Card sx={{
          boxShadow: CARD_SHADOW, borderRadius: 1,
          border: '1px solid', borderColor: 'divider',
          borderLeft: `4px solid ${PRIMARY}`, bgcolor: `${PRIMARY}0A`,
        }}>
          <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
              {fmt(total)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>
              Total Taps (30d)
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{
          boxShadow: CARD_SHADOW, borderRadius: 1,
          border: '1px solid', borderColor: 'divider',
          borderLeft: `4px solid ${ACCENT}`, bgcolor: `${ACCENT}0A`,
        }}>
          <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
              {peak.hour}:00
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>
              Peak Hour
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Card sx={cardBase}>
        <Box sx={sectionHeaderSx}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Tap Distribution by Hour
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            NFC taps across the day (last 30 days)
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="hour" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}:00`} />
              <YAxis tick={{ fontSize: 12 }} />
              <RechartsTooltip labelFormatter={(v) => `${v}:00`} formatter={(value) => [value, 'Taps']} />
              <Bar dataKey="taps" fill={theme.palette.primary.main} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

const QrScanAnalytics = ({ analytics, qrCode, user }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [exportAnchor, setExportAnchor] = useState(null);

  if (!analytics) return (
    <Box sx={{ textAlign: 'center', py: 10 }}>
      <Typography variant="body1" color="text.secondary">Loading QR analytics...</Typography>
    </Box>
  );

  if (!analytics.paid) {
    const totalScans = analytics.totalScans || 0;
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Lock sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>Total Scans: {fmt(totalScans)}</Typography>
        <Typography color="text.secondary" sx={{ mb: 1, maxWidth: 480, mx: 'auto' }}>
          Upgrade to Pro or Business to unlock detailed QR scan analytics — device, location, browser breakdown and more.
        </Typography>
        <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 700, mb: 3 }}>
          Pro: ₹499/year · Business: ₹999/year · + 18% GST
        </Typography>
        <Button variant="contained" href="/subscription"
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0090c7' }, textTransform: 'none', borderRadius: 1.5, fontWeight: 700, px: 4, boxShadow: 'none' }}>
          View Plans & Upgrade
        </Button>
      </Box>
    );
  }

  const tabSections = [
    { label: 'Overview', icon: <Visibility /> },
    { label: 'Geography', icon: <LocationOn /> },
    { label: 'Devices', icon: <Devices /> },
    { label: 'Recent Scans', icon: <Schedule /> },
  ];

  const exportCSV = () => {
    setExportAnchor(null);
    let csv = 'Metric,Value\n';
    csv += `Total Scans,${analytics.totalScans}\n\n`;
    csv += 'Date,Scans\n';
    (analytics.scansByDay || []).forEach(r => { csv += `${fmtDate(r.date)},${r.scans}\n`; });
    csv += '\nCountry,Scans\n';
    (analytics.scansByCountry || []).forEach(r => { csv += `${r.country || 'Unknown'},${r.scans}\n`; });
    csv += '\nDevice,Scans\n';
    (analytics.scansByDevice || []).forEach(r => { csv += `${r.device_type || 'Unknown'},${r.scans}\n`; });
    csv += '\nBrowser,Scans\n';
    (analytics.scansByBrowser || []).forEach(r => { csv += `${r.browser || 'Unknown'},${r.scans}\n`; });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `qr-analytics-${qrCode?.uid || 'export'}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    setExportAnchor(null);
    downloadQrAnalyticsPdf(analytics, qrCode);
  };

  const scansByDay = analytics.scansByDay || [];
  const scansByHour = analytics.scansByHour || [];
  const scansByCountry = analytics.scansByCountry || [];
  const scansByCity = analytics.scansByCity || [];
  const scansByDevice = analytics.scansByDevice || [];
  const scansByBrowser = analytics.scansByBrowser || [];
  const scansByOS = analytics.scansByOS || [];
  const recentScans = analytics.recentScans || [];
  const totalScans = analytics.totalScans || 0;

  const hourlyData = Array.from({ length: 24 }, (_, h) => {
    const found = scansByHour.find(r => r.hour === h);
    return { hour: h, scans: found?.scans || 0 };
  });

  const deviceTotal = scansByDevice.reduce((s, d) => s + d.scans, 0) || 1;

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ ...cardBase, mb: 3 }}>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: `${PRIMARY}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <QrCode sx={{ fontSize: 24, color: PRIMARY }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {qrCode?.label || qrCode?.uid?.slice(0, 16) + '...'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                QR Code → {qrCode?.target_url?.length > 50 ? qrCode.target_url.slice(0, 50) + '...' : qrCode?.target_url}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5}>
            <Button variant="outlined" size="small" startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              onClick={() => window.open(qrCode?.target_url, '_blank')}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500 }}>
              Open Destination
            </Button>
            <Button variant="contained" size="small" startIcon={<FileDownload sx={{ fontSize: 16 }} />}
              onClick={(e) => setExportAnchor(e.currentTarget)}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500 }}>
              Export Data
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { borderRadius: 1, minWidth: 160, mt: 0.5 } }}>
              <MenuItem onClick={exportCSV} sx={{ fontSize: '14px', gap: 1.5 }}>
                <FileDownload sx={{ fontSize: 18, color: '#10B981' }} /> Export as CSV
              </MenuItem>
              <MenuItem onClick={exportPDF} sx={{ fontSize: '14px', gap: 1.5 }}>
                <FileDownload sx={{ fontSize: 18, color: '#EF4444' }} /> Export as PDF
              </MenuItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ mb: 3, minHeight: 42, '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '14px', minHeight: 42, px: 2, mr: 1 } }}>
        {tabSections.map((s, i) => (
          <Tab key={i} label={s.label} icon={s.icon} iconPosition="start" sx={{ minWidth: 'auto' }} />
        ))}
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
            {[
              { value: fmt(totalScans), label: 'Total Scans', color: PRIMARY },
              { value: fmt(scansByCountry.length), label: 'Countries', color: ACCENT },
              { value: scansByDay.length, label: 'Active Days', color: '#F59E0B' },
              { value: scansByHour.length > 0 ? `${scansByHour.reduce((max, h) => h.scans > max.scans ? h : max, { hour: 0, scans: 0 }).hour}:00` : '—', label: 'Peak Hour', color: '#10B981' },
            ].map((kpi, i) => (
              <Card key={i} sx={{ boxShadow: CARD_SHADOW, borderRadius: 1, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${kpi.color}`, bgcolor: `${kpi.color}0A` }}>
                <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>{kpi.value}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>{kpi.label}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {scansByDay.length > 0 && (
            <Card sx={cardBase}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Scans Over Time</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={scansByDay.map(r => ({ date: r.date, scans: r.scans }))}>
                    <defs>
                      <linearGradient id="qrScansGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={PRIMARY} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => fmtDateShort(v)} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip labelFormatter={(v) => fmtDate(v)} formatter={(v) => [v, 'Scans']} />
                    <Area type="monotone" dataKey="scans" stroke={PRIMARY} fill="url(#qrScansGradient)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {scansByHour.length > 0 && (
            <Card sx={{ ...cardBase, mt: 3 }}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Hourly Distribution</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="hour" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}:00`} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <RechartsTooltip labelFormatter={(v) => `${v}:00`} formatter={(v) => [v, 'Scans']} />
                    <Bar dataKey="scans" fill={theme.palette.primary.main} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {scansByCountry.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <LocationOn sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No geographic data yet</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
              <Card sx={cardBase}>
                <Box sx={sectionHeaderSx}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Top Countries</Typography>
                </Box>
                <CardContent sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={tableHeadCellSx}>Country</TableCell>
                          <TableCell align="right" sx={tableHeadCellSx}>Scans</TableCell>
                          <TableCell align="right" sx={tableHeadCellSx}>%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scansByCountry.map((r, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: i === 0 ? PRIMARY : 'text.disabled' }} />
                                <Typography variant="body2" sx={{ fontWeight: i === 0 ? 600 : 400 }}>{r.country || 'Unknown'}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(r.scans)}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2">{totalScans > 0 ? Math.round((r.scans / totalScans) * 100) : 0}%</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
              {scansByCity.length > 0 && (
                <Card sx={cardBase}>
                  <Box sx={sectionHeaderSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Top Cities</Typography>
                  </Box>
                  <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={tableHeadCellSx}>City</TableCell>
                            <TableCell sx={tableHeadCellSx}>Country</TableCell>
                            <TableCell align="right" sx={tableHeadCellSx}>Scans</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scansByCity.map((r, i) => (
                            <TableRow key={i} hover>
                              <TableCell><Typography variant="body2">{r.city || 'Unknown'}</Typography></TableCell>
                              <TableCell><Typography variant="body2" color="text.secondary">{r.country || '—'}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(r.scans)}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {scansByDevice.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Devices sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No device data yet</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Card sx={cardBase}>
                <Box sx={sectionHeaderSx}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Device Metrics</Typography>
                </Box>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {scansByDevice.map((d, i) => {
                      const colors = [PRIMARY, ACCENT, '#F59E0B', '#10B981'];
                      return (
                        <Box key={i} sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${colors[i % colors.length]}`, bgcolor: `${colors[i % colors.length]}06` }}>
                          <Typography variant="h5" fontWeight={700}>{fmt(d.scans)}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '12px', textTransform: 'capitalize' }}>{d.device_type || 'Unknown'}</Typography>
                          <Typography variant="caption" color="text.disabled">{Math.round((d.scans / deviceTotal) * 100)}% of total</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card sx={cardBase}>
                  <Box sx={sectionHeaderSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Browsers</Typography>
                  </Box>
                  <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {scansByBrowser.map((r, i) => (
                            <TableRow key={i} hover>
                              <TableCell><Typography variant="body2" fontWeight={i === 0 ? 600 : 400}>{r.browser || 'Unknown'}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(r.scans)}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
                <Card sx={cardBase}>
                  <Box sx={sectionHeaderSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Operating Systems</Typography>
                  </Box>
                  <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {scansByOS.map((r, i) => (
                            <TableRow key={i} hover>
                              <TableCell><Typography variant="body2" fontWeight={i === 0 ? 600 : 400}>{r.os || 'Unknown'}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(r.scans)}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {activeTab === 3 && (
        <Card sx={cardBase}>
          <Box sx={sectionHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Recent Scans</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Last 50 scan events</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Time', 'Country', 'City', 'Device', 'Browser', 'OS'].map(h => (
                    <TableCell key={h} sx={tableHeadCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {recentScans.length === 0 ? (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>No scans recorded yet</TableCell></TableRow>
                ) : recentScans.map((s, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography variant="caption">{fmtDateTime(s.created_at)}</Typography></TableCell>
                    <TableCell>{s.country || '—'}</TableCell>
                    <TableCell>{s.city || '—'}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{s.device_type || '—'}</TableCell>
                    <TableCell>{s.browser || '—'}</TableCell>
                    <TableCell>{s.os || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};

export default Analytics;
